import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Grid, TextareaAutosize, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import TopSection from "../../LT/TopSection";
import DataDetails from "./DataDetails";
import ButtonComponent from "../../../components/common/button/Button";
import Modal from "../../../components/common/modal/Modal";
import Cards from "../../../components/common/card/Card";
import { fetchData } from "../../../redux/actions/PriceMemo";
import { moveToDataError } from "../../../redux/actions/MoveToDataErrors";
import { moveToContract } from "../../../redux/actions/MoveToContract";
import "./DataErrors/DataErrors.css";
import { Loader } from '../../../components/common/Loader';
import { getAllSourcePriceMemoData } from "../../../redux/actions/GetAllSourcePriceMemo";
import { getAllSourcePricMemo, postArchiveDataEndpoint, postRejectMultipleEndpoint, postUnmappedPlantsDataEndpoint } from "../../../common/endpoints";
import { TabModal } from "./TabModal";
import { editSisPriceMemoApi } from "../../../redux/actions/EditSisPriceMemo";
import { setSelectedItem } from "../../../redux/actions/SupportTicket/selectionSlice";
import UnmappedPlantModal from "./UnmappedPlantModal";
import { unmappedPlantsData } from "../../../redux/actions/PriceMemo/UnmappedPlants";
import { postUnmappedPlantsData } from "../../../redux/actions/PriceMemo/SaveUnmappedPlants";
import { postArchiveData } from "../../../redux/actions/PriceMemo/Archive";
import ArchiveModal from "./ArchiveModal";
import { getArchiveData } from "../../../redux/actions/PriceMemo/GetArchive";
import { postUndoArchive } from "../../../redux/actions/PriceMemo/UndoArchive";
import { postRejectMultiple } from "../../../redux/actions/PriceMemo/RejectMultiple";

const ModalFooter = (props) => {
  return (
    <div className="footer-btn-wrapper">
      <ButtonComponent
        maxWidth="170px"
        label="No"
        bgColor="white"
        color="black"
        onClick={props.onClosePopup}
      />
      <ButtonComponent
        maxWidth="170px"
        label="Yes"
        bgColor="black"
        color="white"
        onClick={props.onSubmit}
        disabled={!props?.errorComment}
      />
    </div>
  );
};

const PriceMemo = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [btnDisabled, setIsBtnDisabled] = useState(true);
  const [open, setOpen] = useState(false);
  const [openmultipleReject, setOpenMultipleReject] = useState(false);
  const [rowsData, setRowsData] = useState([]);
  const [seqId, setSeqId] = useState();
  const [errorComment, setErrorComment] = useState("");
  const [pmSourceData, setPmSourceData] = useState([]);
  const [openSourceModal, setOpenSourceModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otherBtnDisabled, setOtherBtnDisabled] = useState(true);
  const [saveBtnDisabled, setSaveBtnDisabled] = useState(true);
  const [unmappedPlantModal, setUnappedPlantModal] = useState(false);
  const [archivedRecordsModal, setArchivedRecordsModal] = useState(false);
  const [archivedData, setArchivedData] = useState([]);
  const [unmappedPlantData, setUnmappedPlantData] = useState([]);
  const piceMemoDataList = useSelector((state) => state?.piceMemoData);
  const [pmFilteredData, setPMFilteredData] = useState(0);

  const procurementDivisionType = useSelector(
    (state) => state?.userCardType?.procurementDivisionType
  );
  const userEmail = localStorage.getItem("data");
  const userName = localStorage.getItem("userName");
  const userRole = localStorage.getItem("CDRole");
  const plantId = localStorage.getItem("selectedCDPlantCode");

  const handleClose = () => {
    setOpen(false);
    setOpenMultipleReject(false);
  };

  const cleanUsername = (name) => {
    return name.replace(/\s*\[C\]/, ""); // Removes "[C]" with optional leading space
  };

  const handleCellValueChange = async (params) => {
    if (rowsData.some(row => row.runOutputSeqId === params.data.runOutputSeqId)) {
      const updatedSelectedRows = rowsData.map(row =>
        row.runOutputSeqId === params.data.runOutputSeqId ? params.data : row
      );
      setRowsData(updatedSelectedRows);
    }
  };

  useEffect(() => {
    const mandatoryFields = ["sapMaterialNumber", "sapPlantId", "sapSupplierId"];
    const areMandatoryFieldsFilled = rowsData.every((row) =>
      mandatoryFields.every(
        (field) =>
          row[field] &&
          row[field].trim() !== "" &&
          row[field] !== "-"
      )
    );
    setIsBtnDisabled(!(rowsData.length > 0 && areMandatoryFieldsFilled));
  }, [rowsData]);

  const handleSaveSisMaterialId = async () => {
    try {
      const saveSisMaterialIds = await dispatch(
        editSisPriceMemoApi({
          params:{
            userId: userEmail,
            userName: cleanUsername(userName),
            plantId: plantId,
          },
          data: rowsData
        })
      );
      if (saveSisMaterialIds?.payload?.Status === "200") {
        toast.success(saveSisMaterialIds?.payload?.responseMessage, {
          autoClose: 2000,
        });
        dispatch(
          fetchData({
            dynamicEndpoint: `api/cd/pricememo/${procurementDivisionType}`,
            params: {
              plantId: localStorage.getItem("selectedCDPlantCode")
            }
          })
        );
        setOpen(false);
        setIsBtnDisabled(true);
        setSaveBtnDisabled(true)
      } else if (saveSisMaterialIds?.payload?.Status === '206') {
        toast.warn(saveSisMaterialIds?.payload?.responseMessage, {
          autoClose: 3000,
        });
        dispatch(
          fetchData({
            dynamicEndpoint: `api/cd/pricememo/${procurementDivisionType}`,
            params: {
              plantId: localStorage.getItem("selectedCDPlantCode")
            }
          })
        );
        setOpen(false);
        setIsBtnDisabled(true);
        setSaveBtnDisabled(true)
      } else {
        console.error(
          "moveToContract action failed:",
          saveSisMaterialIds?.payload
        );
        toast.error(saveSisMaterialIds?.payload?.responseMessage, {
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.log(error);
    }
  }

  const archiveHandler = async () => {
    setLoading(true);
    const runOutputSeqIds = rowsData?.map((id) => id.runOutputSeqId);
    try {
      const moveToArchive = await dispatch(
        postArchiveData({
          dynamicEndpoint: `${postArchiveDataEndpoint}/${procurementDivisionType}/moveToArchive`,
          params: {
            userEmail: userEmail,
            userId: cleanUsername(userName),
          },
          data: runOutputSeqIds,
        })
      );
      if (moveToArchive.meta.requestStatus === "fulfilled") {
        toast.success("Records Moved to Archive", {
          autoClose: 2000,
        });
        dispatch(
          fetchData({
            dynamicEndpoint: `api/cd/pricememo/${procurementDivisionType}`,
            params: {
              plantId: localStorage.getItem("selectedCDPlantCode")
            }
          })
        );
        setLoading(false);
        setOpen(false);
        setIsBtnDisabled(true);
        setOtherBtnDisabled(true);
        setSaveBtnDisabled(true);
        setRowsData([]);
      } else {
        console.error(
          "moveToContract action failed:",
          moveToArchive.error
        );
        setLoading(false);
        toast.error(`Something went wrong - ${moveToArchive.error}`, {
          autoClose: 2000,
        });
      }
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  }

  const handleMoveDataError = async () => {
    try {
      const moveToDataErrorResponse = await dispatch(
        moveToDataError({
          dynamicEndpoint: `api/cd/pricememo/${procurementDivisionType}/moveToDataError`,
          params: {
            userId: userEmail,
            userName: cleanUsername(userName),
            plantId: plantId,
          },
          data: {
            runOutputSeqId: seqId,
            errorDetails: errorComment,
            isUserDefinedError: true,
          },
        })
      );
      if (moveToDataErrorResponse.meta.requestStatus === "fulfilled") {
        toast.success("Records Moved to Data error", {
          autoClose: 2000,
        });
        dispatch(
          fetchData({
            dynamicEndpoint: `api/cd/pricememo/${procurementDivisionType}`,
            params: {
              plantId: localStorage.getItem("selectedCDPlantCode")
            }
          })
        );
        setOpen(false);
      } else {
        console.error(
          "moveToContract action failed:",
          moveToDataErrorResponse.error
        );
        toast.error(`Something went wrong - ${moveToDataErrorResponse.error}`, {
          autoClose: 2000,
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

    const onSelectionChanged = (params) => {
    const selectedRowsOnCurrentPage = [];
    params?.api?.forEachNodeAfterFilterAndSort((node) => {
      if (node.isSelected()) {
        selectedRowsOnCurrentPage.push(node.data);
      }
    });
    setRowsData(selectedRowsOnCurrentPage);
    dispatch(setSelectedItem(selectedRowsOnCurrentPage));
    
    if(selectedRowsOnCurrentPage?.length > 0) {
       setOtherBtnDisabled(false); 
       setSaveBtnDisabled(false) 
    } else { 
      setOtherBtnDisabled(true); 
      setSaveBtnDisabled(true) 
    }
  };

  const rejectHandler = (id) => {
    setOpen(true);
    setSeqId(id);
  };

  const handleTextAreaChange = (e) => {
    setErrorComment(e.target.value);
  };

  const handleSubmitContract = async () => {
    try {
      const allId = rowsData?.map((id) => id.runOutputSeqId);
      const moveToContractResponse = await dispatch(
        moveToContract({
          dynamicEndpoint: `api/cd/pricememo/${procurementDivisionType}/submitToContract`,
          params: {
            userId: userEmail,
            userName: cleanUsername(userName),
            plantId: plantId,
          },
          data: {
            runOutputSeqIds: allId,
          },
        })
      );
      if (moveToContractResponse.meta.requestStatus === "fulfilled") {
        toast.success("Contract Submitted - In Progress", {
          autoClose: 2000,
        });
        dispatch(
          fetchData({
            dynamicEndpoint: `api/cd/pricememo/${procurementDivisionType}`,
            params: {
              plantId: localStorage.getItem("selectedCDPlantCode")
            }
          })
        );
        setIsBtnDisabled(true);
      } else {
        console.error(
          "moveToContract action failed:",
          moveToContractResponse.payload.errorMessage
        );
        toast.error(`${moveToContractResponse.payload.errorMessage}`, {
          autoClose: 2000,
        });
        setIsBtnDisabled(true);
      }
    } catch (error) {
      console.error("API error:", error);
      setIsBtnDisabled(true);
    }
  };

  const rowSelection = useMemo(() => {
    return {
      mode: "multiRow",
      isRowSelectable: (rowNode) => {
        const status = rowNode.data.status.toLowerCase();
        return status.includes("contract submitted") ||
          status.includes("contract creation failed") ||
          status.includes("contract update successful") ||
          status.includes("news") ||
          status.includes("contract simulation - success") ||
          status.includes("contract creation successful") ||
          status.includes("contract simulation - failed") ||
          userRole === "Viewer"
          ? false
          : true;
      },
    };
  }, []);

  const getRowStyle = (params) => {
    const status = params.data.status.toLowerCase();
    const changedFields = params.data.changedFieldNames;
    if (
      status.includes("contract submitted") ||
      status.includes("contract creation failed") ||
      status.includes("contract update successful") ||
      status.includes("news") ||
      status.includes("contract simulation - success") ||
      status.includes("contract creation successful") ||
      status.includes("contract simulation - failed")
    ) {
      return { backgroundColor: "#aaaaaa" };
    } else if (changedFields && changedFields.length > 0) {
      return { backgroundColor: "#2244CC44" };
    }
    return null;
  };

  useEffect(() => {
    if (procurementDivisionType) {
      setOtherBtnDisabled(true)
      setIsBtnDisabled(true)
    }
  },[procurementDivisionType])

  const fetchAllSourceData = (index, title, url) => {
    if (title === "All Price Memo Records") {
      setOpenSourceModal(true);
      fetchAllPriceMemoSouceData();
    }
    if (title === "Errored Records") {
      navigate('/price-memo/data-errors');
    }
    if (title === "Unmapped Plant Records") {
      setUnappedPlantModal(true);
      fetchAllUnmappedPlants();
    }
    if (title === "Archived Records") {
      setArchivedRecordsModal(true);
      fetchAllArchiveRecords();
    }
  }

  const fetchAllPriceMemoSouceData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dispatch(getAllSourcePriceMemoData({
        dynamicEndpoint: `${getAllSourcePricMemo}/${procurementDivisionType}/getAllRecords`,
        params: {
          plantId: localStorage.getItem("selectedCDPlantCode")
        }
      }));
      if (response?.meta?.requestStatus === 'fulfilled') {
        setLoading(false);
        setPmSourceData(response?.payload)
      } else {
        console.error('Failed to fetch data:', response.error);
      }
    } catch (error) {
      console.error('API error:', error);
    }
  }, [dispatch, procurementDivisionType]);

  const handleSaveUnmappedPlants = async () => {
    setLoading(true);
    const updates = rowsData.map((row) => ({
      id: row?.runOutputSeqId,
      sapPlantId: row?.sapPlantId,
    }));
    try {
      const response = await dispatch(postUnmappedPlantsData({
        dynamicEndpoint: `${postUnmappedPlantsDataEndpoint}?userId=${cleanUsername(userName)}&userEmail=${cleanUsername(userEmail)}`,
        data: { updates }
      }));
      if (response?.meta?.requestStatus === 'fulfilled') {
        setLoading(false);
        toast.success(response?.payload, {
          autoClose: 2000,
        });
        fetchDataFromApi();
        fetchAllUnmappedPlants();
        setSaveBtnDisabled(true);
      } else {
        setLoading(false);
        toast.error(response?.payload?.errorMessage, {
          autoClose: 2000,
        });
        setSaveBtnDisabled(true);
      }
    } catch (error) {
      console.error('API error:', error);
    }
  }

  const handleUndoArchivedRecords = async () => {
    setLoading(true);
    const runOutputSeqIds = rowsData?.map((id) => id.runOutputSeqId);
    try {
      const response = await dispatch(postUndoArchive({
        dynamicEndpoint: `${postArchiveDataEndpoint}/${procurementDivisionType}/undoArchive?userId=${cleanUsername(userName)}&userEmail=${cleanUsername(userEmail)}`,
        data: runOutputSeqIds
      }));
      if (response?.meta?.requestStatus === 'fulfilled') {
        setLoading(false);
        toast.success(response?.payload, {
          autoClose: 2000,
        });
        fetchAllArchiveRecords();
        fetchDataFromApi();
        setSaveBtnDisabled(true);
        setIsBtnDisabled(true);
        setOtherBtnDisabled(true);
        setRowsData([]);
      } else {
        setLoading(false);
        toast.error(response?.payload?.errorMessage, {
          autoClose: 2000,
        });
        setSaveBtnDisabled(true);
      }
    } catch (error) {
      console.error('API error:', error);
    }
  }

  const fetchAllUnmappedPlants = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dispatch(unmappedPlantsData());
      if (response?.meta?.requestStatus === 'fulfilled') {
        setLoading(false);
        setUnmappedPlantData(response?.payload);
      } else {
        console.error('Failed to fetch data:', response.error);
      }
    } catch (error) {
      console.error('API error:', error);
    }
  }, [dispatch]);

  const fetchAllArchiveRecords = useCallback(async () => {
    setLoading(true);
    try {
      const response = await dispatch(getArchiveData({
        dynamicEndpoint: `${postArchiveDataEndpoint}/${procurementDivisionType}/archive/${plantId}`,
      }));
      if (response?.meta?.requestStatus === 'fulfilled') {
        setLoading(false);
        setArchivedData(response?.payload);
      } else {
        console.error('Failed to fetch data:', response.error);
      }
    } catch (error) {
      console.error('API error:', error);
    }
  }, [dispatch, procurementDivisionType, plantId]);

  const fetchDataFromApi = useCallback(async () => {
    try {
      const response = await dispatch(fetchData({
        dynamicEndpoint: `api/cd/pricememo/${procurementDivisionType}`,
        params: {
          plantId: localStorage.getItem("selectedCDPlantCode")
        }
      }));
      if (response?.meta?.requestStatus === 'fulfilled') {
      } else {
        console.error('Failed to fetch data:', response.error);
      }
    } catch (error) {
      console.error('API error:', error);
    }
  }, [dispatch, procurementDivisionType]);

  useEffect(() => {
    fetchDataFromApi();
  }, [fetchDataFromApi]);

  useEffect(() => {
    if (openSourceModal) {
      fetchAllPriceMemoSouceData();
    }
  }, [fetchAllPriceMemoSouceData, openSourceModal]);

  const multipleRejectHandler = async () => {
    try {
      const response = await dispatch(postRejectMultiple({
        dynamicEndpoint: `${postRejectMultipleEndpoint}/${procurementDivisionType}/multipleLinesToDataErrors?userId=${cleanUsername(userName)}&plantId=${localStorage.getItem("selectedCDPlantCode")}`,
        data: {
          runOutputSeqIds: rowsData?.map((id) => id.runOutputSeqId),
          errorDetails: errorComment,
          isUserDefinedError: true,
        },
      }));
      if (response?.meta?.requestStatus === 'fulfilled') {
        setOpenMultipleReject(false);
        toast.success("Selected Line items moved to Data Errors", {
          autoClose: 2000,
        });
        fetchDataFromApi();
        setIsBtnDisabled(true);
        setOtherBtnDisabled(true);
        setSaveBtnDisabled(true);
        setRowsData([]);
      } else {
        toast.error(`${response?.payload?.responseMessage}`, {
          autoClose: 2000,
        });
        console.error('Failed to fetch data:', response.error);
      }
    } catch (error) {
      console.error('API error:', error);
    }
  }

  const rejectMultipleHandler = () => {
    setOpenMultipleReject(true);
  }

  const newChanges = () => (
    <Tooltip title={
        <span style={{fontWeight: 400}}>
          Shows the count of line items where change was recently detected.<br />
          <strong style={{fontWeight: 800}}>Updates:</strong> Total count of line items where the information was updated with new changes in the same line item.<br />
          <strong style={{fontWeight: 800}}>New Additions:</strong> Total count of line items which got newly added to the Price Memo.
        </span>
      }
      >
      <div>
        <p style={{margin: 0}}>Updates : {piceMemoDataList?.data?.recordCount?.updatesCount || 0}</p>
        <p style={{margin: 0}}>New Additions : {piceMemoDataList?.data?.recordCount?.newAdditionCount || 0}</p>
        <p style={{margin: 0}}>Total : {piceMemoDataList?.data?.recordCount?.updatesCount + piceMemoDataList?.data?.recordCount?.newAdditionCount || 0}</p>
      </div>
    </Tooltip>
  );

  const totalChanges = () => (
    <Tooltip title={
        <span style={{fontWeight: 400}}>
          Shows the Total count of changes detected.<br />
          <strong style={{fontWeight: 800}}>Actioned:</strong> Count of line items submitted to Contracts or saved with new information in the Vendor/Plant/Material number columns.<br />
          <strong style={{fontWeight: 800}}>Pending:</strong> Count of line items where no action has been taken yet.
        </span>
      }
      >
    <div>
      <p style={{margin: 0}}>Actioned : {piceMemoDataList?.data?.recordCount?.actionedRecords || 0} </p>
      <p style={{margin: 0}}>Pending : {piceMemoDataList?.data?.recordCount?.pendingRecords || 0} </p>
      <p style={{margin: 0}}>Total : {piceMemoDataList?.data?.recordCount?.actionedRecords + piceMemoDataList?.data?.recordCount?.pendingRecords || 0}</p>
    </div>
    </Tooltip>
  );

  const totalSource = () => (
    <Tooltip title="Shows the count of total records in the source price memo after mapping with the relevant SAP Vendor/Plant/Material Numbers.">
      <div>
        <p style={{margin: 0}}>Total : {piceMemoDataList?.data?.recordCount?.priceMemoSourceRecordCount || 0} </p>
        <p style={{margin: 0}}>Click to view </p>
      </div>
    </Tooltip>
  );

  const unmapped = () => (
    <Tooltip title="Shows the count of line items where SAP plant Number could not be mapped.">
    <div>
      <p style={{margin: 0}}>Total : {piceMemoDataList?.data?.recordCount?.unmappedPlants || 0} </p>
      <p style={{margin: 0}}>Click to view </p>
    </div>
    </Tooltip>
  );

  const archived = () => (
    <Tooltip title="Shows the Total count of Line items Moved to Archive from the Price memo screen.">
    <div>
      <p style={{margin: 0}}>Total : {piceMemoDataList?.data?.recordCount?.archivedPriceMemosCount || 0} </p>
      <p style={{margin: 0}}>Click to view </p>
    </div>
    </Tooltip>
  );

  const errored = () => (
    <Tooltip title="Shows the total count of records that failed the Data Validation Check.">
    <div>
      <p style={{margin: 0}}>Total : {piceMemoDataList?.data?.recordCount?.errorRecordCount || 0} </p>
      <p style={{margin: 0}}>Click to view </p>
    </div>
    </Tooltip>
  );

  return (
    <>
      <TopSection
        labelList={[
          { title: "Home", path: "/landing-page" },
          { title: "Automating Contract Creation" },
        ]}
        hideHeader={true}
        headerName={"Automating Contract Creation"}
      />
      {piceMemoDataList?.loading === true ? (
        <Loader />
      ) : (
        <>
          <p style={{ marginTop: 0 }}>Price Memo {procurementDivisionType}</p>
          <div>
            {
              procurementDivisionType === "SIS" ? (
                <div className="cards-container cd-card-sec">
                  <Cards
                    cards={[
                      {
                        title: "All Price Memo Records",
                        description: totalSource(),
                      },
                      {
                        title: "New Changes",
                        description: newChanges(),
                      },
                      {
                        title: "Total Changes",
                        description: totalChanges(),
                      },
                      {
                        title: "Unmapped Plant Records",
                        description: unmapped(),
                      },
                      {
                        title: "Archived Records",
                        description: archived(),
                      },
                      {
                        title: "Errored Records",
                        description: errored(),
                      },
                    ]}
                    callBackFunction={fetchAllSourceData}
                  />
                </div>
              ):
            <div className="cards-container cd-card-sec">
              <Cards
                cards={[
                  {
                    title: "All Price Memo Records",
                    description: totalSource(),
                  },
                  {
                    title: "New Changes",
                    description: newChanges(),
                  },
                  {
                    title: "Archived Records",
                    description: archived(),
                  },
                  {
                    title: "Total Changes",
                    description: totalChanges(),
                  },
                  {
                    title: "Errored Records",
                    description: errored(),
                  },
                ]}
                callBackFunction={fetchAllSourceData}
              />
            </div>
            }
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <div className="priceMemo_main">
                  <div className="priceMemo_subCls"></div>
                  {!piceMemoDataList.data ? (
                    <div style={{ textAlign: "center", fontWeight: "bold" }}>
                      No record found
                    </div>
                  ) : (
                    <div className="priceMemo_dataTable">
                      <DataDetails
                        onSelectionChanged={onSelectionChanged}
                        rejectHandler={rejectHandler}
                        rowSelection={rowSelection}
                        getRowStyle={getRowStyle}
                        data={piceMemoDataList?.data}
                        handleCellValueChange={handleCellValueChange}
                        setPMFilteredData={setPMFilteredData}
                        userRole={userRole}
                      />
                      <div style={{ margin: "10px 0px 10px 10px", fontWeight: "bold" }}> 
                        Total Records: {pmFilteredData > 0 ? pmFilteredData : (piceMemoDataList?.data?.sisPriceMemo?.length || piceMemoDataList?.data?.agriPriceMemo?.length || piceMemoDataList?.data?.packagingPriceMemo?.length || piceMemoDataList?.data?.cepgPriceMemo?.length || 0)}
                      </div>
                      { userRole !== "Viewer" &&
                      <div
                        style={{
                          marginTop: "50px",
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "30px",
                        }}
                      >
                        <div></div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            width: `${procurementDivisionType === "SIS" ? "480px" : "420px"}`,
                          }}
                        >
                           {procurementDivisionType === "SIS" && ( 
                            <ButtonComponent
                              label="Save"
                              bgColor="#000"
                              color="#fff"
                              disabled={saveBtnDisabled}
                              onClick={handleSaveSisMaterialId}
                            />
                           )}
                           <ButtonComponent
                            label="Archive"
                            bgColor="#000"
                            color="#fff"
                            disabled={otherBtnDisabled}
                            onClick={archiveHandler}
                          />
                          <ButtonComponent
                            label="Reject"
                            bgColor="#000"
                            color="#fff"
                            disabled={otherBtnDisabled}
                            onClick={rejectMultipleHandler}
                          />
                          <ButtonComponent
                            label="Cancel"
                            bgColor="#fff"
                            color="#000"
                            disabled={procurementDivisionType === "SIS" ? btnDisabled : otherBtnDisabled}
                          />
                          <ButtonComponent
                            label="Submit for Contract"
                            bgColor="#000"
                            color="#fff"
                            onClick={handleSubmitContract}
                            disabled={procurementDivisionType === "SIS" ? btnDisabled : otherBtnDisabled}
                          />
                        </div>
                      </div>
                      }
                    </div>
                  )}
                </div>
              </Grid>
            </Grid>
          </div>
        </>
      )}
      <Modal
        open={open}
        handleClose={handleClose}
        title="Comments"
        content={
          <div>
            <TextareaAutosize
              minRows={3}
              style={{ width: "100%" }}
              onChange={handleTextAreaChange}
            />
            <p>Are you sure you want to move this Line item to Data Errors?</p>
          </div>
        }
        action={
          <ModalFooter
            onClosePopup={handleClose}
            onSubmit={handleMoveDataError}
            errorComment={errorComment}
          />
        }
      />
      <Modal
        open={openmultipleReject}
        handleClose={handleClose}
        title="Comments"
        content={
          <div>
            <TextareaAutosize
              minRows={3}
              style={{ width: "100%" }}
              onChange={handleTextAreaChange}
            />
            <p>Are you sure you want to move the selected Line items to Data Errors?</p>
          </div>
        }
        action={
          <ModalFooter
            onClosePopup={handleClose}
            onSubmit={multipleRejectHandler}
            errorComment={errorComment}
          />
        }
      />
      <TabModal
        open={openSourceModal}
        handleClose={() => setOpenSourceModal(false)}
        title="Price Memo Source"
        pmSourceData={pmSourceData}
        getRowStyle={getRowStyle}
        loading={loading}
        fullScreen={true}
      />
      <UnmappedPlantModal 
        open={unmappedPlantModal}
        handleClose={() => setUnappedPlantModal(false)}
        title="Unmapped Plants"
        unmappedPlantData={unmappedPlantData}
        loading={loading}
        onSelectionChanged={onSelectionChanged}
        rowSelection={rowSelection}
        getRowStyle={getRowStyle}
        handleCellValueChange={handleCellValueChange}
        saveBtnDisabled={saveBtnDisabled}
        onSaveUnmappedPlants={handleSaveUnmappedPlants}
        userRole={userRole}
      />
      <ArchiveModal 
        open={archivedRecordsModal}
        handleClose={() => setArchivedRecordsModal(false)}
        title="Archived Records"
        loading={loading}
        archivedData={archivedData}
        onSelectionChanged={onSelectionChanged}
        rowSelection={rowSelection}
        getRowStyle={getRowStyle}
        saveBtnDisabled={saveBtnDisabled}
        onUndoArchivedRecords={handleUndoArchivedRecords}
        userRole={userRole}
      />
    </>
  );
};

export default PriceMemo;
